/**
 * Operator session — Bearer credential sent on mutating POST requests
 * (MARK FIXED / ARCHIVE / REPLY ON TELEGRAM / Log internal response note).
 *
 * C1 fix: the previous version read VITE_OPERATOR_TOKEN at build time, which
 * baked the secret into the public JS bundle (any visitor could extract it
 * with `view-source` or `curl`). The new posture: the operator enters the
 * token at runtime via the "Operator" button in the top bar; the token lives
 * only in this module's memory (not localStorage, not sessionStorage) and is
 * cleared on page reload.
 *
 * Server-side: apps/api/src/index.ts requireOperatorToken() now fails closed
 * when OPERATOR_TOKEN is unset in production, so a misconfigured server
 * returns 503 instead of silently allowing the request through.
 *
 * Where the token comes from: a personal, role-scoped token issued with
 * apps/api/scripts/make-operator-token.ts (server keeps only its SHA-256 in
 * OPERATOR_TOKENS; the legacy shared OPERATOR_TOKEN still works). The bearer
 * format is unchanged. useOperatorIdentity() resolves who is signed in via
 * GET /api/auth/whoami. See docs/security/operator-access.md.
 */

let _sessionToken: string | null = null;
const listeners = new Set<() => void>();

export function setOperatorToken(token: string | null): void {
  _sessionToken = token && token.trim() ? token.trim() : null;
  for (const fn of listeners) fn();
}

export function getOperatorToken(): string | null {
  return _sessionToken;
}

export function hasOperatorToken(): boolean {
  return _sessionToken != null && _sessionToken.length > 0;
}

/** Subscribe to session changes (operator sign-in / sign-out). */
export function subscribeOperatorToken(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Wraps fetch() with the operator Bearer token. On 401, returns the raw
 * Response so the caller can surface "auth required" — doesn't swallow it.
 * On 503, surfaces the server-misconfigured signal so the UI can prompt the
 * operator to re-enter the token (or, in the misconfigured-server case,
 * alert an admin).
 */
export async function operatorFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  if (_sessionToken) {
    headers.set("Authorization", `Bearer ${_sessionToken}`);
  }
  return fetch(input, { ...init, headers });
}
